import React, { useEffect, useRef, useState } from 'react'
import { createChart, IChartApi, ISeriesApi, ColorType, LineData, Time, LineSeries, CandlestickData, CandlestickSeries } from 'lightweight-charts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu'
import { Loader2, BarChart3, TrendingUp, Activity, Settings, Eye, EyeOff } from 'lucide-react'

interface DatasetPreviewChartProps {
  data: Record<string, unknown>[]
  columns: string[]
  isLoading?: boolean
  title?: string
  isMultiSymbol?: boolean
  availableSymbols?: string[]
  selectedSymbol?: string
  onSymbolChange?: (symbol: string) => void
}

export default function DatasetPreviewChart({
  data,
  columns,
  isLoading = false,
  title = 'Dataset Preview',
  isMultiSymbol = false,
  availableSymbols = [],
  selectedSymbol,
  onSymbolChange
}: DatasetPreviewChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<any> | null>(null)
  const [selectedColumn, setSelectedColumn] = useState<string>('')
  const [selectedChartType, setSelectedChartType] = useState<'candlestick' | 'line'>('line')
  const [showGrid, setShowGrid] = useState(true)
  const [showCrosshair, setShowCrosshair] = useState(true)

  // Filter data by selected symbol for multi-symbol datasets
  const filteredData = React.useMemo(() => {
    console.log('🔍 DatasetPreviewChart props:', {
      isMultiSymbol,
      selectedSymbol,
      availableSymbols,
      dataLength: data?.length,
      columns,
      firstRow: data?.[0]
    })

    if (!data || !Array.isArray(data)) {
      return []
    }

    let result = data

    // Note: Preview data filtering is not supported for multi-symbol datasets
    // because preview data may not include the 'symbol' column.
    // Symbol filtering is only applied during actual query execution.
    // Here we just return all data for preview purposes.

    console.log('ℹ️ Preview data for multi-symbol dataset - showing all data (symbol filtering not available in preview)', {
      isMultiSymbol,
      selectedSymbol,
      columns,
      dataLength: data.length,
      hasSymbolColumn: columns?.includes('symbol')
    })

    // If this is a multisymbol dataset and user has selected a specific symbol,
    // but the preview data doesn't have symbol column, we still show all data
    // The actual filtering will happen in the backend during query execution

    // Sort by timestamp to ensure ascending order (required by chart library)
    if (columns && columns.length > 0) {
      const timeColumn = columns.find(col =>
        col.toLowerCase().includes('time') ||
        col.toLowerCase().includes('timestamp') ||
        col.toLowerCase().includes('date')
      ) || columns[0]

      result = [...result].sort((a, b) => {
        const aTime = a[timeColumn]
        const bTime = b[timeColumn]

        if (typeof aTime === 'number' && typeof bTime === 'number') {
          return aTime - bTime
        }

        if (typeof aTime === 'string' && typeof bTime === 'string') {
          return new Date(aTime).getTime() - new Date(bTime).getTime()
        }

        return 0
      })
    }

    return result
  }, [data, isMultiSymbol, selectedSymbol, columns])

  // Use filtered data for chart

  // Early return if no data or columns
  if (!data || !Array.isArray(data) || !columns || !Array.isArray(columns) || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            {isLoading ? 'Loading...' : 'No data available'}
          </div>
        </CardContent>
      </Card>
    )
  }

  // Detect OHLC columns
  const ohlcColumns = React.useMemo(() => {
    if (!columns || !Array.isArray(columns)) {
      return null
    }

    const open = columns.find(col => col.toLowerCase().includes('open'))
    const high = columns.find(col => col.toLowerCase().includes('high'))
    const low = columns.find(col => col.toLowerCase().includes('low'))
    const close = columns.find(col => col.toLowerCase().includes('close'))

    if (open && high && low && close) {
      return { open, high, low, close }
    }

    return null
  }, [columns])

  // Available chart types based on data
  const availableChartTypes = React.useMemo(() => {
    const types: Array<{ value: 'candlestick' | 'line'; label: string; icon: React.ReactNode }> = []

    // Always allow line chart
    types.push({
      value: 'line',
      label: 'Line Chart',
      icon: <TrendingUp className="w-4 h-4" />
    })

    // Add candlestick if OHLC data is available
    if (ohlcColumns) {
      types.push({
        value: 'candlestick',
        label: 'Candlestick',
        icon: <BarChart3 className="w-4 h-4" />
      })
    }

    return types
  }, [ohlcColumns])

  // Set default chart type and column
  useEffect(() => {
    if (availableChartTypes.length > 0 && !selectedChartType) {
      // Prefer candlestick if available, otherwise line
      const defaultType = ohlcColumns ? 'candlestick' : 'line'
      setSelectedChartType(defaultType)
    }
  }, [availableChartTypes, ohlcColumns, selectedChartType])

  // Set default column
  useEffect(() => {
    if (columns.length > 0 && selectedChartType === 'line') {
      if (!selectedColumn) {
        // Find a numeric column for line chart
        const numericColumn = columns.find(col => {
          if (filteredData.length > 0) {
            const value = data[0][col]
            return typeof value === 'number'
          }
          return false
        })
        if (numericColumn) {
          setSelectedColumn(numericColumn)
        } else if (columns.length > 0) {
          setSelectedColumn(columns[0])
        }
      }
    } else if (selectedChartType === 'candlestick') {
      // For candlestick, clear selected column as we use OHLC
      setSelectedColumn('')
    }
  }, [columns, data, selectedColumn, selectedChartType])

  // Initialize chart
  useEffect(() => {
    // Don't initialize chart when no symbol is selected for multi-symbol datasets
    if (isMultiSymbol && (!selectedSymbol || selectedSymbol === '' || selectedSymbol === 'ALL_SYMBOLS')) {
      return
    }

    if (!chartContainerRef.current || isLoading) return

    // Create chart with dark theme
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#1a1a1a' },
        textColor: '#e0e0e0',
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: {
          color: showGrid ? '#404040' : 'transparent',
          style: 1, // LineStyle.Dashed
        },
        horzLines: {
          color: showGrid ? '#404040' : 'transparent',
          style: 1, // LineStyle.Dashed
        },
      },
      width: chartContainerRef.current.clientWidth,
      height: 400,
      timeScale: {
        borderColor: '#404040',
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        borderVisible: true,
        barSpacing: 10,
      },
      rightPriceScale: {
        borderColor: '#404040',
        borderVisible: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
        textColor: '#e0e0e0',
      },
      leftPriceScale: {
        visible: false,
      },
      crosshair: {
        mode: showCrosshair ? 1 : 0, // CrosshairMode.Normal : CrosshairMode.Hidden
        vertLine: {
          width: 1,
          color: '#60a5fa',
          style: 0, // LineStyle.Solid
        },
        horzLine: {
          width: 1,
          color: '#60a5fa',
          style: 0, // LineStyle.Solid
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
      trackingMode: {
        exitMode: 1, // TrackingModeExitMode.OnNextTap
      },
    })

    chartRef.current = chart

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chart) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (chart) {
        chart.remove()
      }
      chartRef.current = null
      seriesRef.current = null
    }
  }, [isLoading, showGrid, showCrosshair, isMultiSymbol, selectedSymbol])

  // Clean up chart when symbol selection changes for multi-symbol datasets
  useEffect(() => {
    if (isMultiSymbol && (!selectedSymbol || selectedSymbol === '' || selectedSymbol === 'ALL_SYMBOLS')) {
      // Clean up existing chart
      if (chartRef.current) {
        chartRef.current.remove()
        chartRef.current = null
        seriesRef.current = null
      }
    }
  }, [isMultiSymbol, selectedSymbol])

  // Update chart data
  useEffect(() => {
    // Don't update chart when no symbol is selected for multi-symbol datasets
    if (isMultiSymbol && (!selectedSymbol || selectedSymbol === '' || selectedSymbol === 'ALL_SYMBOLS')) {
      return
    }

    if (!chartRef.current || !filteredData.length || isLoading) return
    if (selectedChartType === 'line' && !selectedColumn) return

    const chart = chartRef.current

    // Remove existing series
    if (seriesRef.current) {
      chart.removeSeries(seriesRef.current)
      seriesRef.current = null
    }

    // Find time column
    const timeColumn = columns.find(col =>
      col.toLowerCase().includes('time') ||
      col.toLowerCase().includes('timestamp') ||
      col.toLowerCase().includes('date')
    ) || columns[0]

    // Use filteredData (already sorted in the useMemo above)
    const sortedData = filteredData

    // Deduplicate data by timestamp, keeping the last entry for each timestamp
    const deduplicatedData = sortedData.reduce((acc, current) => {
      const timeValue = current[timeColumn]
      const timestamp = typeof timeValue === 'number' ? timeValue :
                       typeof timeValue === 'string' ? new Date(timeValue).getTime() : 0

      // Find if we already have this timestamp
      const existingIndex = acc.findIndex(item => {
        const itemTimeValue = item[timeColumn]
        const itemTimestamp = typeof itemTimeValue === 'number' ? itemTimeValue :
                             typeof itemTimeValue === 'string' ? new Date(itemTimeValue).getTime() : 0
        return itemTimestamp === timestamp
      })

      if (existingIndex >= 0) {
        // Replace with current (keep the last one)
        acc[existingIndex] = current
      } else {
        // Add new entry
        acc.push(current)
      }

      return acc
    }, [] as typeof sortedData)

    // Validate final data has ascending timestamps
    const finalTimestamps = deduplicatedData.map(row => {
      const timeValue = row[timeColumn]
      if (typeof timeValue === 'number') return timeValue
      if (typeof timeValue === 'string') return new Date(timeValue).getTime()
      return 0
    }).filter(t => t > 0)

    for (let i = 1; i < finalTimestamps.length; i++) {
      if (finalTimestamps[i] <= finalTimestamps[i - 1]) {
        console.error('Invalid timestamp order after deduplication, skipping chart render', {
          index: i,
          current: finalTimestamps[i],
          previous: finalTimestamps[i - 1]
        })
        return
      }
    }

    // Use deduplicated data for chart rendering
    const chartData = deduplicatedData

    // Validate data before creating chart
    if (!chartData || chartData.length === 0) {
      console.warn('No valid data for chart rendering')
      return
    }

    if (selectedChartType === 'candlestick' && ohlcColumns) {
      // Create candlestick series with dark theme colors
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#10b981', // Emerald green for up candles
        downColor: '#ef4444', // Red for down candles
        borderVisible: false,
        wickUpColor: '#10b981',
        wickDownColor: '#ef4444',
        borderUpColor: '#10b981',
        borderDownColor: '#ef4444',
      })

      // Convert data to CandlestickData format
      const candlestickData: CandlestickData[] = chartData.map((row, index) => {
        let timeValue: Time

        if (typeof row[timeColumn] === 'string') {
          const date = new Date(row[timeColumn] as string)
          timeValue = Math.floor(date.getTime() / 1000) as Time
        } else if (typeof row[timeColumn] === 'number') {
          timeValue = row[timeColumn] as Time
        } else {
          timeValue = index as Time
        }

        return {
          time: timeValue,
          open: Number(row[ohlcColumns.open]) || 0,
          high: Number(row[ohlcColumns.high]) || 0,
          low: Number(row[ohlcColumns.low]) || 0,
          close: Number(row[ohlcColumns.close]) || 0,
        }
      })

      candlestickSeries.setData(candlestickData)
      seriesRef.current = candlestickSeries

    } else if (selectedChartType === 'line' && selectedColumn) {
      // Create line series with dark theme color
      const lineSeries = chart.addSeries(LineSeries, {
        color: '#60a5fa', // Blue color for line charts
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      })

      // Convert data to LineData format
      const lineData: LineData[] = chartData.map((row, index) => {
        let timeValue: Time

        if (typeof row[timeColumn] === 'string') {
          const date = new Date(row[timeColumn] as string)
          timeValue = Math.floor(date.getTime() / 1000) as Time
        } else if (typeof row[timeColumn] === 'number') {
          timeValue = row[timeColumn] as Time
        } else {
          timeValue = index as Time
        }

        return {
          time: timeValue,
          value: Number(row[selectedColumn]) || 0,
        }
      })

      lineSeries.setData(lineData)
      seriesRef.current = lineSeries
    }

    // Fit content
    chart.timeScale().fitContent()
  }, [filteredData, selectedColumn, columns, isLoading, selectedChartType, ohlcColumns])


  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px]">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Don't show chart when no symbol is selected for multi-symbol datasets
  if (isMultiSymbol && (!selectedSymbol || selectedSymbol === '' || selectedSymbol === 'ALL_SYMBOLS')) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {selectedSymbol === 'ALL_SYMBOLS' ? 'All Symbols Selected' : 'Select a Symbol'}
              </p>
              <p className="text-sm">
                {selectedSymbol === 'ALL_SYMBOLS'
                  ? 'Choose a specific symbol to view individual charts'
                  : 'Choose a symbol to filter data during analysis (preview shows all data)'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!filteredData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
            {isMultiSymbol && (!selectedSymbol || selectedSymbol === '' || selectedSymbol === 'ALL_SYMBOLS') ? (
              <span className="text-sm font-normal text-muted-foreground">
                ({selectedSymbol === 'ALL_SYMBOLS' ? 'All symbols selected' : 'Select symbol for analysis'})
              </span>
            ) : selectedChartType === 'candlestick' ? (
              <span className="text-sm font-normal text-muted-foreground">
                (OHLC Candlesticks)
              </span>
            ) : selectedChartType === 'line' && selectedColumn ? (
              <span className="text-sm font-normal text-muted-foreground">
                (Line Chart: {selectedColumn})
              </span>
            ) : null}
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* Symbol Selector for Multi-Symbol Datasets */}
            {isMultiSymbol && availableSymbols && availableSymbols.length > 0 && (
              <Select value={selectedSymbol || undefined} onValueChange={onSymbolChange}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select symbol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL_SYMBOLS">All Symbols</SelectItem>
                  {availableSymbols.map((symbol) => (
                    <SelectItem key={symbol} value={symbol}>
                      {symbol}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Chart Type Selector - Only show when not viewing all symbols */}
            {!(isMultiSymbol && selectedSymbol === 'ALL_SYMBOLS') && (
              <Select value={selectedChartType} onValueChange={(value: 'candlestick' | 'line') => setSelectedChartType(value)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableChartTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        {type.icon}
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Column Selector for Line Charts - Only show when not viewing all symbols */}
            {selectedChartType === 'line' && !(isMultiSymbol && selectedSymbol === 'ALL_SYMBOLS') && (
              <Select value={selectedColumn} onValueChange={setSelectedColumn}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {columns.filter(col => {
                    if (data.length === 0) return true
                    const value = data[0][col]
                    return typeof value === 'number'
                  }).map((column) => (
                    <SelectItem key={column} value={column}>
                      {column}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Chart Configuration */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Chart Settings</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowGrid(!showGrid)}>
                  {showGrid ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {showGrid ? 'Hide' : 'Show'} Grid
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowCrosshair(!showCrosshair)}>
                  {showCrosshair ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {showCrosshair ? 'Hide' : 'Show'} Crosshair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>


            {/* OHLC Indicator */}
            {selectedChartType === 'candlestick' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                <Activity className="w-3 h-3" />
                OHLC
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isMultiSymbol && !selectedSymbol ? (
          <div className="w-full h-[400px] flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Select a Symbol</p>
              <p className="text-sm">Choose a symbol from the dropdown above to preview the chart</p>
            </div>
          </div>
        ) : (
          <div
            ref={chartContainerRef}
            className="w-full h-[400px]"
          />
        )}
      </CardContent>
    </Card>
  )
}